import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { useHistoricalPlaybackStore } from '../../store/historicalPlaybackStore';
import { useSettingsStore } from '../../store/settingsStore';
import { useCouplingStore } from '../../store/couplingStore';
import { useZoneStore } from '../../store/zoneStore';
import { couplingPosition, exchangeLabel, flowOutOfZone, otherZone, type ZoneCoupling } from '../../types/coupling';
import type { Abgangsleitung, AnyNetworkElement, Coordinates } from '../../types/network';
import { fmt } from '../../utils/numberFormat';
import { puToVolts } from '../../utils/electricalConstants';
import {
  VOLTAGE_BAND_LABEL,
  VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_OPERATIONAL_MIN_PU,
  VOLTAGE_WARNING_HIGH_PU,
  VOLTAGE_WARNING_LOW_PU,
} from '../../utils/voltageConstants';
import { elementDisplayName, shortElementName } from '../../utils/elementDisplayName';
import { parseUtcTimestamp } from '../../utils/timeUtils';

/**
 * Netzzustand-Widget: kompakte, verschiebbare Zusammenfassung des Lastflusses
 * zum aktuell gezeigten Zeitpunkt (Live oder Zeitregler) direkt über der Karte
 * — Trafo, kritischstes Kabel, Spannungsextreme, Bilanz, Austausch über die
 * Trennstellen (Netzverbund), Anzahl auffälliger Elemente. Jede Zeile springt
 * zum Element (Auswahl + Karte zentrieren), so
 * dass sich niemand durch alle Elemente klicken muss, um zu sehen, ob ein
 * Zeitpunkt kritisch war. Wegklickbar auf eine kleine Statuspille, Position
 * und Zustand bleiben (localStorage) erhalten.
 */

type Severity = 'normal' | 'warn' | 'crit';

interface RankedElement {
  element: AnyNetworkElement;
  value: number;
  severity: Severity;
  label: string;
}

interface WidgetState {
  x: number;
  y: number;
  collapsed: boolean;
}

const STORAGE_KEY = 'gridpilot-network-state-widget';
const DEFAULT_STATE: WidgetState = { x: 16, y: 176, collapsed: false };
const WIDGET_WIDTH = 304;

const loadState = (): WidgetState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<WidgetState>;
    return {
      x: typeof parsed.x === 'number' ? parsed.x : DEFAULT_STATE.x,
      y: typeof parsed.y === 'number' ? parsed.y : DEFAULT_STATE.y,
      collapsed: parsed.collapsed === true,
    };
  } catch {
    return DEFAULT_STATE;
  }
};

const saveState = (state: WidgetState) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
};

const worst = (a: Severity, b: Severity): Severity =>
  a === 'crit' || b === 'crit' ? 'crit' : a === 'warn' || b === 'warn' ? 'warn' : 'normal';

const severityText: Record<Severity, string> = {
  normal: 'text-green-600 dark:text-green-400',
  warn: 'text-amber-600 dark:text-amber-400',
  crit: 'text-red-600 dark:text-red-400',
};
const severityDot: Record<Severity, string> = {
  normal: 'bg-green-500',
  warn: 'bg-amber-500',
  crit: 'bg-red-500',
};
const severityLabel: Record<Severity, string> = { normal: 'Normal', warn: 'Warnung', crit: 'Kritisch' };

const cableCenter = (cable: Abgangsleitung): Coordinates | null => {
  const wps = cable.waypoints ?? [];
  if (wps.length > 0) {
    const mid = wps[Math.floor(wps.length / 2)];
    return { lat: mid.lat, lng: mid.lng };
  }
  return cable.coordinates ?? null;
};

export const NetworkStateWidget: React.FC = () => {
  const map = useMap();
  const elements = useNetworkElementsStore(s => s.elements);
  const powerMeasurements = useNetworkElementsStore(s => s.powerMeasurements);
  const segments = useNetworkElementsStore(s => s.segments);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const setHighlightedElement = useNetworkUIStore(s => s.setHighlightedElement);
  const setMapView = useNetworkUIStore(s => s.setMapView);
  const trafoLevels = useSettingsStore(s => s.thresholds.transformerLoadLevels);
  const cableLevels = useSettingsStore(s => s.thresholds.cableLoadLevels);
  const playbackMode = useHistoricalPlaybackStore(s => s.mode);
  const effectiveTs = useHistoricalPlaybackStore(s => s.effectiveTs);
  const isPlaybackLoading = useHistoricalPlaybackStore(s => s.isLoading);
  const meterValuesByElement = useHistoricalPlaybackStore(s => s.meterValuesByElement);
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const couplings = useCouplingStore(s => s.couplings);
  const couplingFlows = useCouplingStore(s => s.flows);
  const selectCoupling = useCouplingStore(s => s.selectCoupling);

  const [state, setState] = useState<WidgetState>(loadState);
  const [estimatedOpen, setEstimatedOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  // Keep map interactions (drag/zoom/click) away from the widget.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [state.collapsed]);

  useEffect(() => { saveState(state); }, [state]);

  const summary = useMemo(() => {
    const byId = new Map(elements.map(e => [e.id, e] as const));
    const resolve = (id: string) => byId.get(id);

    // ── Trafo ────────────────────────────────────────────────────────────
    let trafoTop: RankedElement | null = null;
    let trafoTimestamp = 0;
    for (const el of elements) {
      if (el.type !== 'trafostation') continue;
      const pm = powerMeasurements[el.id];
      if (!pm) continue;
      const loading = (pm as { loadingPercent?: number | null }).loadingPercent ?? null;
      trafoTimestamp = Math.max(trafoTimestamp, pm.timestamp || 0);
      if (loading == null) continue;
      const sev: Severity = loading >= trafoLevels.red ? 'crit' : loading >= trafoLevels.orange ? 'warn' : 'normal';
      if (!trafoTop || loading > trafoTop.value) {
        trafoTop = { element: el, value: loading, severity: sev, label: shortElementName(el) ?? el.id };
      }
    }

    // ── Kabel (max. Segmentauslastung je Kabel) ──────────────────────────
    const cableMax = new Map<string, number>();
    for (const seg of Object.values(segments)) {
      if (seg.loadingPercent == null) continue;
      const cableId = seg.id.includes('_seg') ? seg.id.split('_seg')[0] : seg.id;
      const prev = cableMax.get(cableId);
      if (prev == null || seg.loadingPercent > prev) cableMax.set(cableId, seg.loadingPercent);
    }
    const cables: RankedElement[] = [];
    let cablesWarn = 0;
    let cablesCrit = 0;
    for (const [cableId, loading] of cableMax) {
      const el = byId.get(cableId);
      if (!el || el.type !== 'abgangsleitung') continue;
      const sev: Severity = loading >= cableLevels.red ? 'crit' : loading >= cableLevels.orange ? 'warn' : 'normal';
      if (sev === 'crit') cablesCrit++;
      else if (sev === 'warn') cablesWarn++;
      cables.push({ element: el, value: loading, severity: sev, label: elementDisplayName(el, resolve) });
    }
    cables.sort((a, b) => b.value - a.value);

    // ── Spannungen (Hausanschlüsse) ──────────────────────────────────────
    let vMin: RankedElement | null = null;
    let vMax: RankedElement | null = null;
    let housesWarn = 0;
    let housesCrit = 0;
    let loadKw = 0;
    let exportKw = 0;
    let houseCount = 0;
    let measuredHouseCount = 0;
    let estimatedLoadKw = 0;
    const estimatedHouses: RankedElement[] = [];
    // Mess-/Schätz-Aufteilung: live counts a house as "gemessen" when an SMGW
    // delivers realtime TAF data (same idiom as the map markers' SMGW badge);
    // everything else receives its share of the trafo balance — estimated load.
    // In historical playback the archived frame's meterValues are authoritative
    // (the exact inputs of THAT calculation) — today's SMGW state says nothing
    // about a past frame. Archives older than the meterValues field (2026-08)
    // can't be split; the section is hidden then instead of showing a wrong one.
    // Any non-live frame (archive rewind, Netzanschluss Prüffall) gets its
    // split from meterValues only — a check frame has none, so it stays hidden.
    const historical = playbackMode !== 'live';
    const splitKnown = !historical || meterValuesByElement != null;
    for (const el of elements) {
      if (el.type !== 'hausanschluss') continue;
      const pm = powerMeasurements[el.id] as (typeof powerMeasurements)[string] & {
        voltageL1Pu?: number | null; voltageL2Pu?: number | null; voltageL3Pu?: number | null;
      } | undefined;
      if (!pm) continue;
      houseCount++;
      const hasMeasurement = historical
        ? (meterValuesByElement?.[el.id] ?? []).some(mv => mv.simulated !== true)
        : (el.smgws?.some(s => s.hasRealtimeData && s.currentPowerW != null) ?? false);
      if (hasMeasurement) measuredHouseCount++;
      const p = pm.totalActivePower ?? 0;
      if (p >= 0) {
        loadKw += p;
        if (splitKnown && !hasMeasurement) {
          estimatedLoadKw += p;
          estimatedHouses.push({ element: el, value: p, severity: 'normal', label: shortElementName(el) ?? el.id });
        }
      } else {
        exportKw += -p;
      }
      const phases = [pm.voltageL1Pu, pm.voltageL2Pu, pm.voltageL3Pu].filter((v): v is number => v != null && v > 0);
      const lo = phases.length ? Math.min(...phases) : pm.voltagePU ?? null;
      const hi = phases.length ? Math.max(...phases) : pm.voltagePU ?? null;
      if (lo == null || hi == null) continue;
      const sevLo: Severity = lo < VOLTAGE_OPERATIONAL_MIN_PU ? 'crit' : lo < VOLTAGE_WARNING_LOW_PU ? 'warn' : 'normal';
      const sevHi: Severity = hi > VOLTAGE_OPERATIONAL_MAX_PU ? 'crit' : hi > VOLTAGE_WARNING_HIGH_PU ? 'warn' : 'normal';
      const sev = worst(sevLo, sevHi);
      if (sev === 'crit') housesCrit++;
      else if (sev === 'warn') housesWarn++;
      const name = shortElementName(el) ?? el.id;
      if (!vMin || lo < vMin.value) vMin = { element: el, value: lo, severity: sevLo, label: name };
      if (!vMax || hi > vMax.value) vMax = { element: el, value: hi, severity: sevHi, label: name };
    }

    estimatedHouses.sort((a, b) => b.value - a.value);

    let health: Severity = 'normal';
    if (trafoTop) health = worst(health, trafoTop.severity);
    if (cables[0]) health = worst(health, cables[0].severity);
    if (vMin) health = worst(health, vMin.severity);
    if (vMax) health = worst(health, vMax.severity);

    const hasData = trafoTop != null || cables.length > 0 || vMin != null;
    return { trafoTop, trafoTimestamp, cables, cablesWarn, cablesCrit, vMin, vMax, housesWarn, housesCrit, loadKw, exportKw, houseCount, measuredHouseCount, estimatedLoadKw, estimatedHouses, historical, splitKnown, health, hasData };
  }, [elements, powerMeasurements, segments, trafoLevels, cableLevels, playbackMode, meterValuesByElement]);

  // Austausch über Trennstellen: the coupling cable's flow is the OWNER zone's result
  // (couplingStore.flows, NETZVERBUND.md 5.3), signed from this zone's point of view —
  // positive = Abgabe an die Nachbarzone, negative = Bezug. Live only: an archive frame
  // carries no coupling figures, so the section hides instead of showing stale live values.
  const exchange = useMemo(() => {
    if (!activeZoneId || couplings.length === 0) return null;
    const rows = couplings.map(c => {
      const flow = couplingFlows[c.id];
      return {
        coupling: c,
        neighbour: otherZone(c, activeZoneId),
        outKw: flow ? flowOutOfZone(flow, activeZoneId) : null,
        loading: flow?.loadingPercent ?? null,
      };
    });
    const known = rows.filter(r => r.outKw != null);
    const exportKw = known.reduce((sum, r) => sum + Math.max(0, r.outKw!), 0);
    const importKw = known.reduce((sum, r) => sum + Math.max(0, -r.outKw!), 0);
    rows.sort((a, b) => Math.abs(b.outKw ?? -1) - Math.abs(a.outKw ?? -1));
    return { rows, known: known.length, exportKw, importKw, netKw: exportKw - importKw };
  }, [activeZoneId, couplings, couplingFlows]);

  const instantLabel = useMemo(() => {
    const d = playbackMode === 'historical' && effectiveTs
      ? parseUtcTimestamp(effectiveTs)
      : summary.trafoTimestamp ? new Date(summary.trafoTimestamp) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
  }, [playbackMode, effectiveTs, summary.trafoTimestamp]);

  const jumpTo = useCallback((el: AnyNetworkElement) => {
    const center = el.type === 'abgangsleitung' ? cableCenter(el as Abgangsleitung) : el.coordinates;
    setSelectedElement(el);
    setHighlightedElement(el.id);
    if (center) setMapView(center, Math.max(map.getZoom(), 17));
  }, [map, setSelectedElement, setHighlightedElement, setMapView]);

  const jumpToCoupling = useCallback((c: ZoneCoupling) => {
    const pos = couplingPosition(c);
    selectCoupling(c.id);
    if (pos) setMapView({ lat: pos[0], lng: pos[1] }, Math.max(map.getZoom(), 17));
  }, [map, selectCoupling, setMapView]);

  // ── Dragging (header only) ─────────────────────────────────────────────
  const onDragStart = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: state.x, originY: state.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const container = map.getContainer();
    const maxX = Math.max(0, container.clientWidth - 60);
    const maxY = Math.max(0, container.clientHeight - 40);
    const x = Math.min(maxX, Math.max(0, dragRef.current.originX + e.clientX - dragRef.current.startX));
    const y = Math.min(maxY, Math.max(0, dragRef.current.originY + e.clientY - dragRef.current.startY));
    setState(s => ({ ...s, x, y }));
  };
  const onDragEnd = () => { dragRef.current = null; };

  if (!summary.hasData && !isPlaybackLoading) {
    return null;
  }

  const positionStyle: React.CSSProperties = { left: state.x, top: state.y, width: state.collapsed ? undefined : WIDGET_WIDTH };
  const health = summary.health;

  if (state.collapsed) {
    return (
      <div ref={rootRef} className="absolute z-1000" style={positionStyle}>
        <button
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onClick={() => setState(s => ({ ...s, collapsed: false }))}
          className="flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full shadow-lg border border-gray-200 dark:border-primary-600 bg-white/95 dark:bg-primary-800/95 backdrop-blur-xs text-xs font-medium text-gray-700 dark:text-primary-100 hover:bg-gray-50 dark:hover:bg-primary-700 cursor-pointer select-none touch-none"
          title="Netzzustand einblenden"
        >
          <span className={`w-2.5 h-2.5 rounded-full ${severityDot[health]}`} />
          <span>Netzzustand</span>
          <span className={`font-semibold ${severityText[health]}`}>{severityLabel[health]}</span>
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    );
  }

  const Row = ({ item, valueText, hint, prefix }: { item: RankedElement | null; valueText: (v: number) => string; hint: string; prefix?: string }) => (
    item ? (
      <button
        onClick={() => jumpTo(item.element)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-primary-700 text-left group cursor-pointer"
        title={`${hint} — ${item.label}: auf der Karte auswählen`}
      >
        <span className="min-w-0 flex items-center gap-1 text-[11px] text-gray-700 dark:text-primary-200 group-hover:text-accent-primary">
          {prefix && <span className="shrink-0 text-gray-400 dark:text-primary-500">{prefix}</span>}
          <span className="truncate">{item.label}</span>
        </span>
        <span className={`shrink-0 text-xs font-semibold tabular-nums ${severityText[item.severity]}`}>{valueText(item.value)}</span>
      </button>
    ) : (
      <div className="px-2 py-1 text-[11px] text-gray-400 dark:text-primary-500">–</div>
    )
  );

  return (
    <div
      ref={rootRef}
      className="absolute z-1000 rounded-lg shadow-xl border border-gray-200 dark:border-primary-600 bg-white/95 dark:bg-primary-800/95 backdrop-blur-xs text-gray-800 dark:text-primary-100 select-none"
      style={positionStyle}
    >
      {/* Header — drag handle */}
      <div
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-primary-600 cursor-move touch-none"
        title="Ziehen zum Verschieben"
      >
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${severityDot[health]}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold leading-tight">
            Netzzustand · <span className={severityText[health]}>{severityLabel[health]}</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-primary-400 leading-tight truncate">
            {isPlaybackLoading ? 'Lastfluss wird geladen …' : instantLabel ? (
              <>
                {playbackMode === 'historical' && (
                  <span className="font-semibold text-amber-600 dark:text-amber-400">Archiv · </span>
                )}
                {playbackMode === 'check' && (
                  <span className="font-semibold text-info-600 dark:text-info-400">Prüffall · </span>
                )}
                Lastfluss {instantLabel}
              </>
            ) : 'Kein Lastfluss'}
          </div>
        </div>
        <button
          onClick={() => setState(s => ({ ...s, collapsed: true }))}
          className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-primary-100 hover:bg-gray-100 dark:hover:bg-primary-700 cursor-pointer"
          title="Widget einklappen"
          aria-label="Netzzustand einklappen"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-2 py-2 space-y-2">
        {/* Bilanz */}
        <div className="grid grid-cols-2 gap-1.5 px-1">
          <div className="rounded bg-gray-50 dark:bg-primary-700/60 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400">Bezug</div>
            <div className="text-sm font-semibold tabular-nums text-info-600 dark:text-info-400">{fmt(summary.loadKw, 1)} kW</div>
            {summary.splitKnown && summary.loadKw > 0 && summary.estimatedLoadKw > 0 && (
              <div
                className="mt-1 mb-0.5 h-1 rounded-full overflow-hidden flex"
                title={`Gemessen ${fmt(summary.loadKw - summary.estimatedLoadKw, 1)} kW · geschätzt ${fmt(summary.estimatedLoadKw, 1)} kW`}
              >
                <div className="h-full bg-info-500" style={{ width: `${(1 - summary.estimatedLoadKw / summary.loadKw) * 100}%` }} />
                <div className="h-full flex-1 bg-gray-300 dark:bg-primary-500" />
              </div>
            )}
          </div>
          <div className="rounded bg-gray-50 dark:bg-primary-700/60 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400">Einspeisung</div>
            <div className="text-sm font-semibold tabular-nums text-success-600 dark:text-success-400">{fmt(summary.exportKw, 1)} kW</div>
          </div>
        </div>

        {/* Geschätzter Bezug (Häuser ohne Messwert, aus der Trafo-Bilanz verteilt).
            Hidden when the split is unknowable (old archive frame without meterValues). */}
        {summary.splitKnown && summary.estimatedHouses.length > 0 && (
          <div className="px-1">
            <button
              onClick={() => setEstimatedOpen(o => !o)}
              className="w-full flex items-center justify-between gap-2 px-1 py-1 rounded hover:bg-gray-100 dark:hover:bg-primary-700 text-left cursor-pointer"
              title={summary.historical
                ? 'Bezug ohne Messwert im archivierten Lastfluss: an der Trafostation gemessene Last, die bilanziell auf die Hausanschlüsse ohne eigenen Zählerwert verteilt wurde'
                : 'Bezug ohne SMGW-Messung: an der Trafostation gemessene Last, die bilanziell auf die Hausanschlüsse ohne Livemessung verteilt wird'}
            >
              <span className="flex items-center gap-1 min-w-0 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-primary-400">
                <svg className={`w-3 h-3 shrink-0 transition-transform ${estimatedOpen ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="truncate">{summary.historical ? 'Davon geschätzt (ohne Messwert)' : 'Davon geschätzt (ohne SMGW)'}</span>
                <span className="shrink-0 normal-case tracking-normal font-normal text-gray-400 dark:text-primary-500">· {summary.estimatedHouses.length} HA</span>
              </span>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-700 dark:text-primary-200">
                {fmt(summary.estimatedLoadKw, 1)} kW
                {summary.loadKw > 0 && (
                  <span className="font-normal text-gray-400 dark:text-primary-500"> · {fmt(summary.estimatedLoadKw / summary.loadKw * 100, 0)} %</span>
                )}
              </span>
            </button>
            {estimatedOpen && (
              <div className="mt-0.5 ml-2 pl-1 border-l border-gray-200 dark:border-primary-600 max-h-44 overflow-y-auto scrollbar-thin">
                {summary.estimatedHouses.map(h => (
                  <button
                    key={h.element.id}
                    onClick={() => jumpTo(h.element)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-primary-700 text-left group cursor-pointer"
                    title={`Geschätzter Bezug — ${h.label}: auf der Karte auswählen`}
                  >
                    <span className="min-w-0 truncate text-[11px] text-gray-700 dark:text-primary-200 group-hover:text-accent-primary">{h.label}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-gray-500 dark:text-primary-400">{fmt(h.value, 2)} kW</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Austausch über Trennstellen (Netzverbund) — collapsed by default like "Davon geschätzt";
            the short label keeps the net figure readable in the 304 px widget */}
        {exchange && !summary.historical && (
          <div className="px-1">
            <button
              onClick={() => setExchangeOpen(o => !o)}
              className="w-full flex items-center justify-between gap-2 px-1 py-1 rounded hover:bg-gray-100 dark:hover:bg-primary-700 text-left cursor-pointer"
              title="Wirkleistung, die über die Trennstellen an andere Netzgebiete abgegeben oder von dort bezogen wird — Ergebnis der Nachbarzonen, Vorzeichen aus Sicht dieser Zone"
            >
              <span className="flex items-center gap-1 min-w-0 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-primary-400">
                <svg className={`w-3 h-3 shrink-0 transition-transform ${exchangeOpen ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="truncate">Trennstellen</span>
                <span className="shrink-0 normal-case tracking-normal font-normal text-gray-400 dark:text-primary-500">· {exchange.rows.length}</span>
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${
                exchange.known === 0 || Math.abs(exchange.netKw) < 0.05
                  ? 'text-gray-400 dark:text-primary-500'
                  : exchange.netKw > 0 ? 'text-success-600 dark:text-success-400' : 'text-info-600 dark:text-info-400'
              }`}>
                {exchange.known === 0
                  ? '–'
                  : Math.abs(exchange.netKw) < 0.05
                    ? 'ausgeglichen'
                    : exchange.netKw > 0
                      ? `Netto-Abgabe ${fmt(exchange.netKw, 1)} kW`
                      : `Netto-Bezug ${fmt(-exchange.netKw, 1)} kW`}
              </span>
            </button>
            {exchangeOpen && (
              <div className="mt-0.5 ml-2 pl-1 border-l border-gray-200 dark:border-primary-600 max-h-44 overflow-y-auto scrollbar-thin">
                {exchange.known > 0 && (
                  <div className="px-2 py-0.5 flex items-center justify-between text-[10px] text-gray-500 dark:text-primary-400">
                    <span>Abgabe <span className="font-semibold text-success-600 dark:text-success-400 tabular-nums">{fmt(exchange.exportKw, 1)} kW</span></span>
                    <span>Bezug <span className="font-semibold text-info-600 dark:text-info-400 tabular-nums">{fmt(exchange.importKw, 1)} kW</span></span>
                  </div>
                )}
                {exchange.rows.map(r => (
                  <button
                    key={r.coupling.id}
                    onClick={() => jumpToCoupling(r.coupling)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-primary-700 text-left group cursor-pointer"
                    title={`${exchangeLabel(r.outKw, r.neighbour.name)} — Trennstelle ${r.coupling.switchElementName ?? r.coupling.switchElementId}: auf der Karte zeigen`}
                  >
                    <span className="min-w-0 flex items-center gap-1 text-[11px] text-gray-700 dark:text-primary-200 group-hover:text-accent-primary">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.coupling.isClosed ? 'bg-green-500' : 'bg-orange-500'}`} />
                      <span className="truncate">{r.neighbour.name}</span>
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-gray-700 dark:text-primary-200">
                      {r.outKw == null
                        ? <span className="text-gray-400 dark:text-primary-500">{r.coupling.isClosed ? 'kein Ergebnis' : 'offen'}</span>
                        : Math.abs(r.outKw) < 0.05
                          ? <span className="text-gray-400 dark:text-primary-500">0 kW</span>
                          : <>{r.outKw > 0 ? '→' : '←'} {fmt(Math.abs(r.outKw), 1)} kW</>}
                      {r.loading != null && <span className="text-gray-400 dark:text-primary-500"> · {fmt(r.loading, 0)} %</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trafo */}
        <section>
          <div className="px-2 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-primary-400">Trafo</div>
          <Row item={summary.trafoTop} valueText={v => `${fmt(v, 1)} %`} hint="Trafoauslastung" />
        </section>

        {/* Kabel */}
        <section>
          <div className="px-2 flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-primary-400">
            <span>Höchste Kabelauslastung</span>
            {(summary.cablesWarn > 0 || summary.cablesCrit > 0) && (
              <span className="normal-case tracking-normal font-normal">
                {summary.cablesCrit > 0 && <span className="text-red-600 dark:text-red-400">{summary.cablesCrit} ≥ {fmt(cableLevels.red, 0)} %</span>}
                {summary.cablesCrit > 0 && summary.cablesWarn > 0 && ' · '}
                {summary.cablesWarn > 0 && <span className="text-amber-600 dark:text-amber-400">{summary.cablesWarn} ≥ {fmt(cableLevels.orange, 0)} %</span>}
              </span>
            )}
          </div>
          {summary.cables.slice(0, 3).map(c => (
            <Row key={c.element.id} item={c} valueText={v => `${fmt(v, 1)} %`} hint="Kabelauslastung" />
          ))}
          {summary.cables.length === 0 && <div className="px-2 py-1 text-[11px] text-gray-400 dark:text-primary-500">–</div>}
        </section>

        {/* Spannung */}
        <section>
          <div className="px-2 flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-primary-400">
            <span>Spannung (L-N)</span>
            {(summary.housesWarn > 0 || summary.housesCrit > 0) && (
              <span className="normal-case tracking-normal font-normal">
                {summary.housesCrit > 0 && <span className="text-red-600 dark:text-red-400">{summary.housesCrit} außerhalb {VOLTAGE_BAND_LABEL}</span>}
                {summary.housesCrit > 0 && summary.housesWarn > 0 && ' · '}
                {summary.housesWarn > 0 && <span className="text-amber-600 dark:text-amber-400">{summary.housesWarn} grenznah</span>}
              </span>
            )}
          </div>
          <Row item={summary.vMin} prefix="↓" valueText={v => `${fmt(puToVolts(v), 1)} V`} hint="Niedrigste Spannung" />
          <Row item={summary.vMax} prefix="↑" valueText={v => `${fmt(puToVolts(v), 1)} V`} hint="Höchste Spannung" />
        </section>

        <div className="px-2 pt-1 border-t border-gray-100 dark:border-primary-700 text-[10px] text-gray-400 dark:text-primary-500">
          {summary.houseCount} Hausanschlüsse{summary.splitKnown ? ` (${summary.measuredHouseCount} ${summary.historical ? 'mit Messwert' : 'mit SMGW-Messung'})` : ''} · Klick auf eine Zeile wählt das Element auf der Karte
        </div>
      </div>
    </div>
  );
};
